import React, {
  useEffect, useRef, useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import {
  createScope, createTimeline, onScroll,
} from 'animejs';
import endpoints from '../constants/endpoints';
import Social from './Social';
import FallbackSpinner from './FallbackSpinner';
import SkeletonWatchScene from './SkeletonWatchScene';
import useReducedEffects from '../hooks/useReducedEffects';

function Home() {
  const [data, setData] = useState(null);
  const sceneRef = useRef(null);
  const reducedEffects = useReducedEffects();

  useEffect(() => {
    Promise.all([
      endpoints.home,
      endpoints.about,
      endpoints.skills,
      endpoints.education,
      endpoints.experiences,
      endpoints.projects,
    ].map((endpoint) => fetch(endpoint, { method: 'GET' }).then((res) => res.json())))
      .then(([home, about, skills, education, experience, projects]) => {
        setData({
          home, about, skills, education, experience, projects,
        });
      })
      .catch((err) => err);
  }, []);

  useEffect(() => {
    if (!data) return undefined;
    document.dispatchEvent(new Event('portfolio-ready'));

    const scrollToHash = () => {
      if (!window.location.hash) return;
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          document.querySelector(window.location.hash)?.scrollIntoView();
        });
      });
    };

    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, [data]);

  useEffect(() => {
    const root = sceneRef.current;
    const intro = root?.querySelector('.scroll-chapters');

    if (!root || !intro || !data || reducedEffects) {
      return undefined;
    }

    const scope = createScope({ root }).add(() => {
      createTimeline({
        autoplay: onScroll({
          target: intro,
          enter: 'top top',
          leave: 'bottom bottom',
          sync: 0.18,
        }),
        defaults: { ease: 'inOut(3)' },
      })
        .add('.light-beam--one', {
          x: ['-22vw', '18vw', '-14vw', '24vw'],
          rotate: [-26, -8, 18, -20],
          opacity: [0.05, 0.25, 0.08, 0.21],
          duration: 4800,
        }, 0)
        .add('.light-beam--two', {
          x: ['28vw', '-16vw', '20vw', '-22vw'],
          rotate: [30, 8, -18, 24],
          opacity: [0.04, 0.17, 0.26, 0.07],
          duration: 4800,
        }, 0)
        .add('.scene-bloom', {
          x: ['20vw', '-18vw', '12vw', '-8vw'],
          y: ['-15vh', '12vh', '-8vh', '6vh'],
          scale: [0.65, 1.25, 0.8, 1.4],
          opacity: [0.12, 0.34, 0.16, 0.31],
          duration: 4800,
        }, 0)
        .add('.cinematic-scroll__progress-line', {
          scaleY: [0, 1],
          duration: 4800,
          ease: 'linear',
        }, 0)
        .add('.scroll-chapter--intro .scroll-chapter__content', {
          opacity: [1, 0],
          y: [0, -90],
          duration: 820,
        }, 420)
        .add('.scroll-chapter--craft .scroll-chapter__content', {
          opacity: [0, 1, 1, 0],
          y: [90, 0, 0, -90],
          duration: 1500,
        }, 900)
        .add('.scroll-chapter--systems .scroll-chapter__content', {
          opacity: [0, 1, 1, 0],
          y: [90, 0, 0, -90],
          duration: 1500,
        }, 2200)
        .add('.scroll-chapter--destination .scroll-chapter__content', {
          opacity: [0, 1],
          y: [90, 0],
          duration: 900,
        }, 3650);

      root.querySelectorAll('.portfolio-section').forEach((section) => {
        createTimeline({
          autoplay: onScroll({
            target: section,
            enter: 'top 85%',
            leave: 'bottom 15%',
            sync: 0.18,
          }),
          defaults: { ease: 'out(3)' },
        }).add(section.querySelector('.portfolio-section__content'), {
          y: [48, 0],
          duration: 700,
        });
      });
    });

    return () => scope.revert();
  }, [data, reducedEffects]);

  const {
    home, about, skills, education, experience, projects,
  } = data || {};

  return data ? (
    <section
      ref={sceneRef}
      className={[
        'cinematic-scroll',
        reducedEffects ? 'cinematic-scroll--reduced' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="cinematic-scroll__stage">
        <div className="cinematic-scroll__grid" aria-hidden="true" />
        <div className="light-beam light-beam--one" aria-hidden="true" />
        <div className="light-beam light-beam--two" aria-hidden="true" />
        <div className="scene-bloom" aria-hidden="true" />
        <SkeletonWatchScene reducedEffects={reducedEffects} />
        <div className="cinematic-scroll__coordinates" aria-hidden="true">
          <span>41.9028° N</span>
          <span>12.4964° E</span>
        </div>
        <div className="cinematic-scroll__progress" aria-hidden="true">
          <span>SCROLL / 04</span>
          <i><b className="cinematic-scroll__progress-line" /></i>
        </div>
      </div>

      <div className="scroll-chapters">
        <article id="home" data-nav-section="home" className="scroll-chapter scroll-chapter--intro">
          <div className="scroll-chapter__content">
            <p className="scroll-chapter__eyebrow">01 / Movement exposed</p>
            <h1>{home.name}</h1>
            <div className="scroll-chapter__roles">
              {home.roles.map((role) => <span key={role}>{role}</span>)}
            </div>
            <p className="scroll-chapter__lead">
              I build digital products that feel precise, responsive and alive.
            </p>
            <span className="scroll-cue">
              Scroll to navigate
              <b>↓</b>
            </span>
          </div>
        </article>

        <article className="scroll-chapter scroll-chapter--craft">
          <div className="scroll-chapter__content">
            <p className="scroll-chapter__eyebrow">02 / Gear train engaged</p>
            <h2>
              Ideas become
              <br />
              tactile systems.
            </h2>
            <p>
              Frontend architecture, motion and product detail shaped into experiences
              that remain fast beneath the surface.
            </p>
            <div className="scroll-chapter__tags">
              <span>React</span>
              <span>Mobile</span>
              <span>Motion</span>
            </div>
          </div>
        </article>

        <article className="scroll-chapter scroll-chapter--systems">
          <div className="scroll-chapter__content">
            <p className="scroll-chapter__eyebrow">03 / Case assembly</p>
            <h2>
              Beyond the
              <br />
              visible layer.
            </h2>
            <p>
              I connect thoughtful interfaces to dependable APIs, release flows and
              the engineering decisions that make products last.
            </p>
            <div className="scroll-chapter__metric">
              <strong>WEB / MOBILE / SYSTEMS</strong>
              <span>One connected product mindset</span>
            </div>
          </div>
        </article>

        <article className="scroll-chapter scroll-chapter--destination">
          <div className="scroll-chapter__content">
            <p className="scroll-chapter__eyebrow">04 / Watch complete</p>
            <h2>
              See where the
              <br />
              journey leads.
            </h2>
            <div className="cinematic-hero__actions">
              <a className="cinematic-button cinematic-button--primary" href="#projects">
                Explore projects
                <span aria-hidden="true">↗</span>
              </a>
              <a className="cinematic-button" href="#about">About me</a>
            </div>
            <Social />
          </div>
        </article>
      </div>

      <div className="portfolio-sections">
        <section id="about" data-nav-section="about" className="portfolio-section portfolio-section--about">
          <div className="portfolio-section__content">
            <header className="portfolio-section__header">
              <span>05 / Profile</span>
              <h2>About</h2>
            </header>
            <div className="portfolio-about">
              <div className="portfolio-copy"><ReactMarkdown>{about.about}</ReactMarkdown></div>
              <figure className="portfolio-portrait">
                <img src={about.imageSource} alt={about.imageAlt || 'Davide Onano'} />
                <figcaption>Operator profile / Italy</figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section id="skills" data-nav-section="skills" className="portfolio-section portfolio-section--skills">
          <div className="portfolio-section__content">
            <header className="portfolio-section__header">
              <span>06 / Capabilities</span>
              <h2>Skills</h2>
              <ReactMarkdown>{skills.intro}</ReactMarkdown>
            </header>
            <div className="capability-rails">
              {skills.skills.map((group, groupIndex) => (
                <article className="capability-rail" key={group.title}>
                  <span>
                    0
                    {groupIndex + 1}
                  </span>
                  <h3>{group.title}</h3>
                  <div>
                    {group.items.map((item) => (
                      <span className="capability-item" key={item.title}>
                        <img src={item.icon} alt="" />
                        {item.title}
                      </span>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="education" data-nav-section="education" className="portfolio-section">
          <div className="portfolio-section__content">
            <header className="portfolio-section__header">
              <span>07 / Formation</span>
              <h2>Education</h2>
            </header>
            <div className="orbital-timeline">
              {education.education.map((item) => (
                <article key={`${item.title}-${item.cardTitle}`}>
                  <time>{item.title}</time>
                  <div>
                    <h3>{item.cardTitle}</h3>
                    <p>{item.cardSubtitle}</p>
                    {item.cardDetailedText && <small>{item.cardDetailedText}</small>}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="experience" data-nav-section="experience" className="portfolio-section">
          <div className="portfolio-section__content">
            <header className="portfolio-section__header">
              <span>08 / Field log</span>
              <h2>Experience</h2>
            </header>
            <div className="orbital-timeline orbital-timeline--experience">
              {experience.experiences.map((item) => (
                <article key={`${item.title}-${item.dateText}`}>
                  <time>{item.dateText}</time>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.subtitle}</p>
                    {item.workType && <small>{item.workType}</small>}
                    <ul>
                      {item.workDescription.map((point) => (
                        <li key={point}><ReactMarkdown components={{ p: 'span' }}>{point}</ReactMarkdown></li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="projects" data-nav-section="projects" className="portfolio-section portfolio-section--projects">
          <div className="portfolio-section__content">
            <header className="portfolio-section__header">
              <span>09 / Selected work</span>
              <h2>Projects</h2>
            </header>
            <div className="project-scenes">
              {projects.projects.map((project, index) => (
                <article className="project-scene" key={project.title}>
                  <div className="project-scene__visual">
                    <img src={project.image} alt="" />
                    <span>
                      0
                      {index + 1}
                    </span>
                  </div>
                  <div className="project-scene__copy">
                    <h3>{project.title}</h3>
                    <ReactMarkdown>{project.bodyText}</ReactMarkdown>
                    <div className="project-scene__tags">
                      {project.tags?.map((tag) => <span key={tag}>{tag}</span>)}
                    </div>
                    <div className="project-scene__links">
                      {project.links?.map((link) => (
                        <a key={link.href} href={link.href} target="_blank" rel="noreferrer">
                          {link.text}
                          <span aria-hidden="true">↗</span>
                        </a>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>
    </section>
  ) : <FallbackSpinner />;
}

export default Home;
